import { useFanEvent } from '@/hooks/useFanEvent';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';
import { useParamsSafe } from '@/hooks/useParamsSafe';
import { useAuth } from '@/hooks/useAuth';
import { ErrorScreen } from '@/components/ErrorScreen';
import { FullScreenLoader } from '@/components/Loader';
import { ScreenThemeStateProvider } from '@/providers/ScreenThemeStateProvider';
import { EventScreenThemeProvider } from '@/providers/ScreenThemeStateProvider/EventScreenThemeProvider';
import { FanActiveEngagement } from '@/engagements/FanActiveEngagement';
import { useFanStyles } from './useFanStyles';
import { useScreenTheme } from '@/providers/ScreenThemeStateProvider/EventScreenThemeContext';
import { useGoogleFonts } from '@/hooks/useGoogleFonts';
import { useHotkeys } from '@shelf/hotkeys';
import { FanElement } from './FanElement';
import {
  deleteScreenElement,
  selectScreenElement,
} from '@/providers/sharedActions';
import { EngagementMode } from '@/types/screen';
import { FanGuideEngagement } from './FanGuideEngagement';
import useLocalStorage from 'use-local-storage';
import { QUICK_SIGNUP_ROUTE } from '@/routes/consts';
import { ThemeOverride } from '@/providers/ThemeOverride';
import { Role } from '@/generated/graphql';

export function EventFanScreen() {
  const { id } = useParamsSafe('id');
  const [searchParams] = useSearchParams();
  const editor =
    searchParams.get('editor') === 'true' &&
    searchParams.get('preview') !== 'true';
  const isPreview = searchParams.get('preview') === 'true';
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [engagementMode, setEngagementMode] = useLocalStorage<EngagementMode>(
    'engagementMode',
    isPreview ? EngagementMode.None : EngagementMode.Actual,
  );

  const { data, loading, error } = useFanEvent(id, {
    skip: !user,
  });

  if (!user) {
    return (
      <Navigate
        to={`${QUICK_SIGNUP_ROUTE}?returnUrl=${encodeURIComponent(pathname + location.search)}`}
      />
    );
  }

  if (error) {
    return <ErrorScreen error={error} />;
  }

  if (loading) {
    return <FullScreenLoader />;
  }

  if (!data?.event) {
    return <ErrorScreen error={error} />;
  } else if (!data.event.live && user.role === Role.Anon) {
    return <ErrorScreen error={new Error('Event is not live')} />;
  }

  return (
    <ScreenThemeStateProvider
      event={data.event}
      screenType="fan"
      engagementMode={editor ? engagementMode : EngagementMode.Actual}
      setEngagementMode={setEngagementMode}
    >
      <EventScreenThemeProvider>
        <Screen />
      </EventScreenThemeProvider>
    </ScreenThemeStateProvider>
  );
}

function Screen() {
  const [searchParams] = useSearchParams();
  const editor =
    searchParams.get('editor') === 'true' &&
    searchParams.get('preview') !== 'true';
  const { state, dispatch } = useScreenTheme();

  const { rootStyles } = useFanStyles({
    state,
  });

  useGoogleFonts({
    fontFamily:
      state.draftTheme?.bodyFontFamily ||
      state.savedTheme?.bodyFontFamily ||
      [],
  });

  useGoogleFonts({
    fontFamily:
      state.draftTheme?.headingFontFamily ||
      state.savedTheme?.headingFontFamily ||
      [],
  });

  useHotkeys({
    Backspace: useCallback(() => {
      if (editor && state.selectedElementId) {
        dispatch(deleteScreenElement({ id: state.selectedElementId }));
      }
    }, [editor, state.selectedElementId, dispatch]),
  });

  const handleDeselectAll = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const dataName = (e.target as HTMLElement).getAttribute('data-name');
      if (editor && !dataName?.startsWith('BOUNDING_BOX')) {
        dispatch(selectScreenElement({ id: undefined }));
      }
    },
    [editor, dispatch],
  );

  return (
    <ThemeOverride theme="dark">
      <div
        data-name="FAN_ROOT"
        className={`relative flex min-h-screen w-screen flex-col gap-4 pt-[10vh]`}
        style={rootStyles}
        onClick={handleDeselectAll}
      >
        <div data-name="FAN_ENGAGEMENT" className="relative z-10">
          {state.engagementMode === EngagementMode.Actual &&
            state.activeEngagement && (
              <FanActiveEngagement engagement={state.activeEngagement} />
            )}
          {state.engagementMode === EngagementMode.Guide && (
            <FanGuideEngagement />
          )}
        </div>

        <div
          data-name="FAN_SCREEN_ELEMENTS"
          className={`absolute left-0 top-0 h-full w-full ${editor ? 'z-20' : 'z-0'} `}
        >
          {Object.keys(state.savedTheme.fanElements).map((elementId) => (
            <FanElement key={elementId} elementId={elementId} editor={editor} />
          ))}
        </div>
      </div>
    </ThemeOverride>
  );
}
